"use client";

import React from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import { useNews } from '@/data/news';

export default function NewsSection() {
  const { t } = useTranslation();
  const { newsList } = useNews();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: true,
  });

  const scrollPrev = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  return (
    <section className="bg-brand-blue text-white py-16 px-6" id="news">
      <div className="container mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
          <h2 className="text-4xl font-bold">{t("news.title")}</h2>
          <div className="flex gap-3">
            <button
              onClick={scrollPrev}
              className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={scrollNext}
              className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Next slide"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex -mx-4">
            {newsList.map((news, index) => (
              <div key={index} className="flex-[0_0_100%] min-w-0 sm:flex-[0_0_80%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%] px-4">
                <Link href={`/news/${news.id_noticia}`} className="block">
                  <div className="group cursor-pointer">
                    <div className="relative h-[250px] sm:h-[300px] overflow-hidden rounded-lg">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img 
                        src={news.imagem_noticia} 
                        alt={news.nome_noticia} 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                    </div>
                    <div className="mt-4">
                      <p className="text-gray-300 text-sm mb-2">{news.data_noticia}</p>
                      <h3 className="text-xl font-semibold mb-4 line-clamp-2 group-hover:text-brand-orange transition-colors">
                        {news.nome_noticia}
                      </h3>
                      <span 
                        className="inline-flex items-center text-brand-orange hover:text-brand-orange/80 transition-colors"
                      >
                        <span>{t("news.learn_more")}</span>
                        <ArrowUpRight className="ml-1 w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
